<template lang="pug">
.media-wrap(
  ref="mediaWrapRef"
  :class="[mobileCfg && 'has-mobile']"
  @click="$emit('click', $event)"
  :style="{maxWidth: (config || {}).maxWidth ? config.maxWidth+'px' : false}"

)
  StaticImage.mobile(v-if="mobileCfg" v-bind="mobileCfg" :fetchPriority="fetchPriority")
  StaticImage(v-if="desktopCfg" v-bind="desktopCfg" :fetchPriority="fetchPriority")
  RogueLink.media-link(
    v-if="config && config.link"
    :to="config.link"
    :target="newTab ? '_blank' : null"
    :aria-label="config.ariaLabel"
    tabindex="-1"
    aria-hidden="true"
  )
  .content(:class="[theme && theme]" v-if="$slots.default")
    slot
  .text-overlay(v-if="config?.overlay || config?.mobile?.overlay")
    | {{ __(config?.overlay || config?.mobile?.overlay) }}
</template>
<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useResizeObserver } from '@vueuse/core'
import type { MediaConfig } from '../../js/sisuTypes'

const DEFAULT_WIDTHS = [360, 521, 768, 1284, 1960]

const props = withDefaults(defineProps<{
  // Media configuration
  config: MediaConfig,
  // Default is to `cover` the full area of the div
  contain?: boolean,
  // Content theme - Hero, CTA, etc
  theme?: string,
  newTab?: boolean,
  // Whether to start this image with a specific load - use only for above the fold media elements
  preload?: number,
  // Add aria-label for link text - shouldn't be the same as image alt text, this will describe the link destination if needed
  ariaLabel?: string,
  widths?: number[],
  fetchPriority?: 'high' | 'low' | 'auto'
}>(), {
  contain       : false,
  newTab        : false,
  widths        : () => [],
  fetchPriority : 'auto',
})
  
defineEmits(['click'])

const mediaWrapRef = ref<HTMLElement | null>(null)
const width = ref(0)

function getBoundAttrs(cfg: any, containerWidth: number, isMobile = false): object | null {
  if (!(cfg || {}).src) {
    return null
  }

  let max = props.config?.maxWidth
  if (typeof max === 'string') {
    max = parseInt(max, 10)
  }

  let widths: number[] = props.widths.concat(DEFAULT_WIDTHS)
  if (max) {
    containerWidth = Math.min(max, containerWidth)
    widths.push(max)
    widths = widths.filter(w => w <= max)
  }

  const imgNativeWidth = cfg.width || 1920
  const imgNativeHeight = cfg.height || imgNativeWidth * 0.5625

  // Dont load an image larger than the source
  containerWidth = (containerWidth || !props.preload) ? Math.min(imgNativeWidth, containerWidth) : props.preload

  const sortedWidths = widths.sort((a, b) => a - b)
  const loadWidth = sortedWidths.find(width => containerWidth <= width) || sortedWidths[sortedWidths.length - 1]

  const loadHeight = Math.round((loadWidth / imgNativeWidth) * imgNativeHeight)

  return {
    // CMS images without alt text are treated as decorative rather than announced by file name
    alt                  : cfg.alt || props.config.alt || '',
    width                : loadWidth,
    imgWidth             : cfg.width,
    height               : loadHeight,
    autoHeight           : cfg.autoHeight,
    src                  : cfg.src,
    enableVideoControls  : cfg.enableVideoControls,
    overlay              : cfg.overlay,
    eager                : props.preload ? true : undefined,
    contain              : props.contain ? '' : undefined,
    background           : cfg.background ? cfg.background : undefined,
    additionalTransforms : cfg.gravity ? [`g_${cfg.gravity}`] : undefined,
  }
}

const mobileCfg = computed<object | null>(() => props.config?.mobile ? getBoundAttrs(props.config.mobile, width.value, true) : null)
const desktopCfg = computed<object | null>(() => getBoundAttrs(props.config, width.value))

let rafId: number | null = null

// Debounce function to prevent excessive updates
function debounce<T extends (...args: any[]) => void>(func: T, delay: number): T {
  let timeoutId: ReturnType<typeof setTimeout>

  return ((...args: any[]) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func.apply(null, args), delay)
  }) as T
}

const handleResize = debounce((entries: readonly ResizeObserverEntry[]) => {
  // Cancel any pending animation frame
  if (rafId !== null) {
    cancelAnimationFrame(rafId)
  }
  
  // Use requestAnimationFrame to defer the update
  rafId = requestAnimationFrame(() => {
    const newWidth = entries[0].contentRect.width
    // Only update if the width has actually changed significantly
    if (Math.abs(newWidth - width.value) > 1) {
      width.value = newWidth
    }
    rafId = null
  })
}, 16)

useResizeObserver(mediaWrapRef, handleResize)

onMounted(() => {
  width.value = mediaWrapRef.value?.getBoundingClientRect().width || 0
})

onBeforeUnmount(() => {
  // Cancel any pending animation frame
  if (rafId !== null) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
})

defineExpose({
  $el: mediaWrapRef,
})
</script>
<style lang="scss">

// This is in a non-scoped scss tag to apply deep
.media-wrap > .content:not(.normal-head) {
  h1 {
    font-size: 36px;
  }

  h2 {
    font-size: 30px;
  }

  @include all-desktop() {
    h1, h2, h3, h4, h5 {
      span {
        font-size: 200%;
      }
    }
  }
}

.media-wrap > .rg-img {
  width: 100%;
}
</style>

<style lang="scss" scoped>
.media-wrap {
  position: relative;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  display: flex;
}

.media-wrap > :deep(video) {
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
}

@include mobile() {
  .has-mobile > :deep(.rg-img:not(.mobile)) {
    display: none;
  }
}

@include not-mobile() {
  .mobile {
    display: none;
  }
}

.text-overlay {
  position: absolute;
  bottom: 15px;
  left: 15px;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 2;
  padding: $space-xxs $space-xxs;
  font-size: $size-xxs;

  @include mobile {
    bottom: 5px;
    left: 5px;
    font-size: 10px;
  }
}

.media-wrap > .content {
  z-index: 1;
  width: 100%;
  margin-left: -100%;
  justify-content: center;
  flex-direction: column;
  color: white;
  align-items: flex-start;
  display: flex;
  padding: $space-s;
  pointer-events: none;

  > * {
    pointer-events: all;
  }

  /*
  Dynamic Media Themes
   */

  &.center {
    justify-content: center;
    align-items: center;
  }

  &.center-desktop {
    @include all-desktop() {
      justify-content: center;
      align-items: center;
      text-align: center;
    }
  }

  &.center-mobile {
    @include not-desktop() {
      justify-content: center;
      align-items: center;
      text-align: center;
    }
  }

  &.none {
    width: 100%;
  }

  &.none-desktop {
    @include all-desktop() {
      width: 100%;
    }
  }

  &.none-mobile {
    @include not-desktop() {
      width: 100%;
    }
  }

  &.right {
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
  }

  &.right-desktop {
    @include all-desktop() {
      flex-direction: row;
      align-items: center;
      justify-content: flex-end;
    }
  }

  &.right-mobile {
    @include not-desktop() {
      flex-direction: row;
      align-items: center;
      justify-content: flex-end;
    }
  }

  &.bottom-left {
    justify-content: flex-end;

    @include all-desktop() {
      padding: $space-l;
    }
  }

  &.bottom-left-desktop {
    @include all-desktop() {
      padding: $space-l;
      justify-content: flex-end;
    }
  }

  &.bottom-left-mobile {
    @include not-desktop() {
      justify-content: flex-end;
    }
  }

  &.absolute {
    padding: 0;
  }

  &.top-left {
    justify-content: flex-start;
  }

  &.top-left-desktop {
    @include all-desktop() {
      justify-content: flex-start;
    }
  }

  &.top-left-mobile {
    @include not-desktop() {
      justify-content: flex-start;
    }
  }

  &.top-right {
    justify-content: flex-end;
  }

  &.top-right-desktop {
    @include all-desktop() {
      justify-content: flex-end;
    }
  }

  &.top-right-mobile {
    @include not-desktop() {
      justify-content: flex-end;
    }
  }

  &.bottom-right {
    justify-content: flex-end;
    align-items: flex-end;
    text-align: right;
  }

  &.bottom-right-desktop {
    @include all-desktop() {
      justify-content: flex-end;
      align-items: flex-end;
      text-align: right;
    }
  }

   &.bottom-right-mobile {
    @include not-desktop() {
      justify-content: flex-end;
      align-items: flex-end;
      text-align: right;
    }
   }

  &.horiz-gradient {
    background: linear-gradient(to right, rgba(#000, 0.7), rgba(#000, 0))
  }
}

// `A` tag that overlays the image
.media-wrap > .media-link {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.001;
  background: transparent;
  &:focus {
    opacity: 1;
    color: transparent;
    border: 2px solid black;
  }
}
</style>
